import { InfoCircleOutlined } from "@ant-design/icons"
import { Alert, Button, Skeleton, Tooltip, Typography } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { formatElapsed } from "../../helpers/elapsed.ts"
import type { Loadable } from "../../hooks/use-tracking-overview.ts"
import type { Measure, TrackingSummary } from "../../types/tracking.types.ts"

const { Text } = Typography

function Figure({
  label,
  definition,
  value,
  aside
}: {
  label: string
  /** What the server counts, said in words; shown on the label's hint. */
  definition: string
  value: string
  aside: string
}) {
  return (
    <div className="min-w-[160px] flex-1 border-0 border-l border-solid border-l-slate-200 px-4 py-1 first:border-l-0 first:pl-0">
      <Text type="secondary" className="block text-xs">
        {label}
        <Tooltip title={definition}>
          <InfoCircleOutlined
            aria-label={definition}
            className="ml-1 text-slate-400"
          />
        </Tooltip>
      </Text>
      <Text strong className="text-base">
        {value}
      </Text>
      <Text type="secondary" className="block text-xs">
        {aside}
      </Text>
    </div>
  )
}

const percent = (rate: number | null) =>
  rate == null ? "—" : `${Math.round(rate * 100)}%`

/**
 * How the requests on screen moved — the same tab, filters and branch as the
 * table, over every matching request rather than the loaded page.
 *
 * A median counts only intervals that ended; "—" means none has yet, which is
 * said in words, and a failed load is never shown as a figure.
 */
export function TrackingSummaryStrip({
  summary,
  scope,
  onRetry
}: {
  summary: Loadable<TrackingSummary>
  /** Which requests these figures are about, in words. */
  scope: string
  onRetry?: () => void
}) {
  const { t } = useTranslation()
  if (summary.status === "error") {
    return (
      <Alert
        type="warning"
        showIcon
        message={t("esign.tracking.strip.unavailable")}
        action={
          onRetry ? (
            <Button size="small" onClick={onRetry}>
              {t("esign.common.retry")}
            </Button>
          ) : undefined
        }
      />
    )
  }
  const data = summary.data
  if (!data) {
    return (
      <div aria-busy aria-label={t("esign.tracking.strip.loading")}>
        <Skeleton active paragraph={{ rows: 1 }} title={false} />
      </div>
    )
  }

  const median = (measure: Measure) => formatElapsed(measure.median_seconds, t)
  // A median with nothing measured says why, and still counts what is running.
  const aside = (measure: Measure, none: string, running: string) =>
    [
      measure.measured
        ? t("esign.tracking.strip.basedOn", { count: measure.measured })
        : none,
      measure.running ? t(running, { count: measure.running }) : null
    ]
      .filter(Boolean)
      .join(" · ")

  return (
    <div className="flex flex-col gap-2">
      <Text type="secondary" className="text-xs" role="note">
        {scope}
      </Text>
      <div
        data-testid="tracking-summary-strip"
        className="flex flex-wrap gap-y-3 rounded-lg bg-slate-50 px-4 py-3"
      >
        <Figure
          label={t("esign.tracking.strip.firstOpen")}
          definition={t("esign.tracking.strip.firstOpenHelp")}
          value={median(data.time_to_first_open)}
          aside={aside(
            data.time_to_first_open,
            t("esign.tracking.strip.noneOpened"),
            "esign.tracking.strip.notOpenedYet"
          )}
        />
        <Figure
          label={t("esign.tracking.strip.cycleTime")}
          definition={t("esign.tracking.strip.cycleTimeHelp")}
          value={median(data.cycle_time)}
          aside={aside(
            data.cycle_time,
            t("esign.tracking.strip.noneCompleted"),
            "esign.tracking.strip.stillOut"
          )}
        />
        <Figure
          label={t("esign.tracking.strip.ourDelay")}
          definition={t("esign.tracking.strip.ourDelayHelp")}
          value={median(data.our_delay)}
          aside={aside(
            data.our_delay,
            t("esign.tracking.strip.noneCountersigned"),
            "esign.tracking.strip.waitingOnUs"
          )}
        />
        <Figure
          label={t("esign.tracking.strip.openRate")}
          definition={t("esign.tracking.strip.openRateHelp")}
          value={percent(data.open_rate.rate)}
          aside={
            data.open_rate.sent
              ? t("esign.tracking.strip.openedOf", {
                  part: data.open_rate.opened,
                  count: data.open_rate.sent
                })
              : t("esign.tracking.strip.noneSent")
          }
        />
        <Figure
          label={t("esign.tracking.strip.declineRate")}
          definition={t("esign.tracking.strip.declineRateHelp")}
          value={percent(data.decline_rate.rate)}
          aside={
            data.decline_rate.finished
              ? t("esign.tracking.strip.declinedOf", {
                  part: data.decline_rate.declined,
                  count: data.decline_rate.finished
                })
              : t("esign.tracking.strip.noneFinished")
          }
        />
      </div>
    </div>
  )
}
